import type { CommonInputProps } from "@frui.ts/bootstrap/dist/controls/commonInputProps";
import { bound } from "@frui.ts/helpers";
import { getValidationMessage } from "@frui.ts/validation";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { Observer } from "mobx-react-lite";
import React from "react";
import type { FormControlProps } from "react-bootstrap";
import { Form } from "react-bootstrap";
import localizationProvider from "../localization/localizationProvider";
import type { IBindingProps } from "@frui.ts/views";
import { BindingComponent } from "@frui.ts/views";
import { czechTimeZone, endOfCzechDay, startOfCzechDay } from "../utils/dateUtils";
import type { DateRange } from "@edaz/edazkit/DateRangeInput";
import DateRangeInput from "@edaz/edazkit/DateRangeInput";

export default class DateRangeInputControl<TTarget> extends BindingComponent<
  TTarget,
  FormControlProps & CommonInputProps & IBindingProps<TTarget> & { customPlaceholder?: string } & { max?: Date } & { min?: Date }
> {
  state = {
    invalidDate: false,
  };

  protected handleValueChanged = (range: DateRange) => {
    let from, to;
    let invalidDate = false;

    if (range.from) {
      const parsedFrom = new Date(range.from);

      if (isNaN(parsedFrom.getTime())) {
        invalidDate = true;
      } else {
        from = startOfCzechDay(zonedTimeToUtc(parsedFrom, czechTimeZone));
      }
    }

    if (range.to) {
      const parsedTo = new Date(range.to);
      if (isNaN(parsedTo.getTime())) {
        invalidDate = true;
      } else {
        to = endOfCzechDay(zonedTimeToUtc(parsedTo, czechTimeZone));
      }
    }

    if (invalidDate) {
      this.setState({ invalidDate: true });
      return;
    } else {
      this.setState({ invalidDate: false });
    }

    let value: DateRange | undefined = { from, to };
    if (from === undefined && to === undefined) {
      value = undefined;
    }

    this.setValue(value);
  };

  formatValue(value: any) {
    let from, to;
    if (value?.from) {
      from = utcToZonedTime(value.from, czechTimeZone);
    }
    if (value?.to) {
      to = utcToZonedTime(value.to, czechTimeZone);
    }
    return from === undefined && to === undefined ? undefined : { from, to };
  }

  get invalidDateMessage() {
    return this.state.invalidDate ? localizationProvider.translateGeneral("invalidDate") : undefined;
  }

  @bound protected renderInner() {
    const { target, property, customPlaceholder } = this.props as {
      target: TTarget;
      property: keyof TTarget & string;
      customPlaceholder?: string;
      max?: Date;
      min?: Date;
    };
    const { noValidation, errorMessage, disabled } = this.inheritedProps;
    const validationError =
      noValidation !== true && (errorMessage || getValidationMessage(target, property) || this.invalidDateMessage);

    return (
      <>
        <DateRangeInput
          localization={
            customPlaceholder
              ? localizationProvider.dateRangeInputCustomLocalization(customPlaceholder)
              : localizationProvider.dateRangeInputLocalization
          }
          onChange={this.handleValueChanged}
          value={this.formatValue(this.value)}
          className={validationError ? "is-invalid" : ""}
          disabled={disabled}
        />
        {validationError && (
          <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
            {validationError}
          </Form.Control.Feedback>
        )}
      </>
    );
  }

  render() {
    return <Observer render={this.renderInner} />;
  }
}
